import React, { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, XCircle, AlertTriangle, Circle, ChevronDown, ChevronUp, ChevronRight, ArrowUpDown, Search, Filter, UploadCloud, Folder, FolderOpen, X, File, Trash2, RefreshCw } from 'lucide-react'
import PageHeader from '../components/PageHeader'
import Button from '../components/Button'
import IconButton from '../components/IconButton'
import Chip from '../components/Chip'
import { useAuth } from '../hooks/useAuth'
import { config } from '../config'
import { apiService } from '../services/api'

type DomainStatus = 'connected' | 'issues' | 'not_connected' | 'not_supported' | 'coming_soon'
type OverallStatus = 'active' | 'paused' | 'coming_soon' | 'not_connected' | 'none' | 'needs_review'
type JobState = 'idle' | 'discovery_running' | 'needs_review' | 'preparing' | 'active' | 'paused' | 'attention'
const FRIENDLY_STATUS: Record<JobState, string> = {
  idle: 'Not connected',
  discovery_running: 'Setting up insights…',
  needs_review: 'Action needed',
  preparing: 'Preparing your view…',
  active: 'Live',
  paused: 'Paused',
  attention: 'Needs attention',
}

type Datasource = {
  id: string
  name: string
  logo: string
  description: string
  supports: { product: boolean; customer: boolean }
  status: { product: DomainStatus; customer: DomainStatus }
  lastSyncTs?: string
  pipelineHealth?: 'healthy' | 'degraded' | 'failing'
  lastError?: string
}

const STATUS_ICON: Record<DomainStatus, (label?: string) => JSX.Element> = {
  connected: (label = 'Connected') => (
    <span title={label} aria-label={label} role="img"><CheckCircle className="w-4 h-4 text-green-600" /></span>
  ),
  issues: (label = 'Issues') => (
    <span title={label} aria-label={label} role="img"><AlertTriangle className="w-4 h-4 text-red-600" /></span>
  ),
  not_connected: (label = 'Not connected') => (
    <span title={label} aria-label={label} role="img"><Circle className="w-4 h-4 text-gray-400" /></span>
  ),
  not_supported: (label = 'Not supported') => (
    <span className="text-gray-300" title={label} aria-label={label}>—</span>
  ),
  coming_soon: (label = 'Coming soon') => (
    <span title={label} aria-label={label} role="img"><Circle className="w-4 h-4 text-purple-500" /></span>
  ),
}

// Full-word chips for domain-specific Product/Customer status
const DOMAIN_STATUS_CHIP: Record<DomainStatus, (label?: string) => JSX.Element> = {
  connected: (label = 'Connected') => (
    <span title={label} className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200 font-semibold">Connected</span>
  ),
  issues: (label = 'Issues') => (
    <span title={label} className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200 font-semibold">Issues</span>
  ),
  not_connected: (label = 'Not Connected') => (
    <span title={label} className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200 font-semibold">Not Connected</span>
  ),
  not_supported: (label = 'Not Supported') => (
    <span title={label} className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-gray-50 text-gray-400 border-gray-200 font-semibold">Not Supported</span>
  ),
  coming_soon: (label = 'Coming Soon') => (
    <span title={label} className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-purple-50 text-purple-700 border-purple-200 font-semibold">Coming Soon</span>
  ),
}

const OVERALL_CHIP: Record<OverallStatus, (label?: string) => JSX.Element> = {
  active: (label = 'Active') => (
    <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200" title={label}>{label}</span>
  ),
  paused: (label = 'Paused') => (
    <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-slate-50 text-slate-700 border-slate-200" title={label}>{label}</span>
  ),
  coming_soon: (label = 'Coming Soon') => (
    <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-purple-50 text-purple-700 border-purple-200" title={label}>{label}</span>
  ),
  not_connected: (label = 'Not Connected') => (
    <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200" title={label}>{label}</span>
  ),
  none: (label = '-') => (
    <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-gray-50 text-gray-500 border-gray-200" title={label}>{label}</span>
  ),
  needs_review: (label = 'Needs Review') => (
    <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200" title={label}>{label}</span>
  ),
}

function getOverallStatus(ds: Datasource): OverallStatus {
  if (ds.status.product === 'coming_soon' || ds.status.customer === 'coming_soon') return 'coming_soon'
  const supported = ds.supports.product || ds.supports.customer
  const connected = ds.status.product === 'connected' || ds.status.customer === 'connected'
  if (!supported) return 'none'
  if (!connected) return 'not_connected'
  if (ds.pipelineHealth === 'healthy') return 'active'
  if (ds.pipelineHealth === 'degraded' || ds.pipelineHealth === 'failing') return 'needs_review'
  return 'active'
}

function humanize(ts?: string) {
  if (!ts) return 'Never'
  const d = new Date(ts)
  const diff = Date.now() - d.getTime()
  const min = 60 * 1000, hr = 60 * min, day = 24 * hr, week = 7 * day
  if (diff < hr) return `${Math.max(1, Math.floor(diff / min))} min ago`
  if (diff < day) return `${Math.floor(diff / hr)} hour${Math.floor(diff / hr) > 1 ? 's' : ''} ago`
  if (diff < week) return `${Math.floor(diff / day)} day${Math.floor(diff / day) > 1 ? 's' : ''} ago`
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}

function statusChipForDomain(status: DomainStatus): JSX.Element {
  if (status === 'connected') return <Chip variant="success" size="xs">Connected</Chip>
  if (status === 'issues') return <Chip variant="warning" size="xs">Needs attention</Chip>
  if (status === 'coming_soon') return <Chip variant="info" size="xs">Coming soon</Chip>
  if (status === 'not_supported') return <Chip variant="muted" size="xs">Not supported</Chip>
  return <Chip variant="default" size="xs">Not connected</Chip>
}

function scheduleChip(text: string): JSX.Element {
  return <Chip variant="info" size="xs">{text}</Chip>
}

const mockDatasources: Datasource[] = [
  {
    id: 'google',
    name: 'Google',
    logo: '/images/google.svg',
    description: 'Docs, emails and calendars for product evidence and customer engagements',
    supports: { product: true, customer: true },
    status: { product: 'not_connected', customer: 'not_connected' }, // Will be overridden by real status
  },
  {
    id: 'notion',
    name: 'Notion',
    logo: '/images/notion.svg',
    description: 'Product specs and docs to enrich feature catalog and roadmaps',
    supports: { product: true, customer: false },
    status: { product: 'not_connected', customer: 'not_supported' }, // Will be overridden by real status
  },
  // Legacy datasources - not currently supported by backend
  // {
  //   id: 'jira',
  //   name: 'Jira',
  //   logo: '/images/jira.svg',
  //   description: 'Issues and epics to map feature backlog and progress',
  //   supports: { product: true, customer: false },
  //   status: { product: 'not_connected', customer: 'not_supported' },
  // },
  // {
  //   id: 'github',
  //   name: 'GitHub',
  //   logo: '/images/github.svg',
  //   description: 'PRs and repos to validate feature implementation and activity',
  //   supports: { product: true, customer: false },
  //   status: { product: 'not_connected', customer: 'not_supported' },
  // },
  {
    id: 'slack',
    name: 'Slack',
    logo: '/images/slack.svg',
    description: 'Product and customer conversations for insight signals',
    supports: { product: true, customer: true },
    status: { product: 'not_connected', customer: 'not_connected' }, // Will be overridden by real status
  },
  // {
  //   id: 'confluence',
  //   name: 'Confluence',
  //   logo: '/images/confluence.svg',
  //   description: 'Knowledge base and release notes to support product insights',
  //   supports: { product: true, customer: false },
  //   status: { product: 'not_connected', customer: 'not_supported' },
  // },
  // {
  //   id: 'highspot',
  //   name: 'Highspot',
  //   logo: '/images/highspot.svg',
  //   description: 'Sales collateral performance to inform product-market fit',
  //   supports: { product: true, customer: false },
  //   status: { product: 'coming_soon', customer: 'not_supported' },
  // },
  {
    id: 'salesforce',
    name: 'Salesforce',
    logo: '/images/salesforce.svg',
    description: 'Core account, opportunity, and engagement view for customers',
    supports: { product: false, customer: true },
    status: { product: 'not_supported', customer: 'not_connected' }, // Will be overridden by real status
  },
  // {
  //   id: 'hubspot',
  //   name: 'HubSpot',
  //   logo: '/images/hubspot.svg',
  //   description: 'CRM alternative for customer pipeline and engagement',
  //   supports: { product: false, customer: true },
  //   status: { product: 'not_supported', customer: 'not_connected' },
  // },
  {
    id: 'gong',
    name: 'Gong',
    logo: '/images/gong.svg',
    description: 'Call intelligence to extract customer signals',
    supports: { product: false, customer: true },
    status: { product: 'not_supported', customer: 'not_connected' },
  },
  // {
  //   id: 'webhose',
  //   name: 'Webhose (News/API)',
  //   logo: '/images/webhose.svg',
  //   description: 'External news feeds to enrich customer/account context',
  //   supports: { product: false, customer: true },
  //   status: { product: 'not_supported', customer: 'coming_soon' },
  // },
]

export default function Settings() {
  const { user, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [filter, setFilter] = useState<'all' | 'product' | 'customer' | 'connected' | 'issues' | 'needs_setup'>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // MVP: Only integrations tab for now
  const [activeTab, setActiveTab] = useState<'integrations' | 'notifications' | 'dashboard'>('integrations')
  const [detailsTab, setDetailsTab] = useState<'summary' | 'mapping' | 'history'>('summary')
  const [historySubTab, setHistorySubTab] = useState<'product' | 'customer'>('product')
  const [jobStateById, setJobStateById] = useState<Record<string, JobState>>({})
  const [toast, setToast] = useState<{ id: number; message: string } | null>(null)
  const [successBanner, setSuccessBanner] = useState<{ datasource: string; status: 'analyzing' | 'completed' } | null>(null)
  
  // 🆕 Real OAuth status from backend
  const [realConnectionStatus, setRealConnectionStatus] = useState<Record<string, any>>({})
  
  // Folder configuration modal state
  const [showFolderConfig, setShowFolderConfig] = useState(false)
  const [folderConfigTab, setFolderConfigTab] = useState<'product' | 'customer'>('product')
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [folders, setFolders] = useState<Record<'product' | 'customer', Array<{
    folder_id: string
    folder_name: string
    sample_files?: string[]
    children?: Array<{ folder_id: string; folder_name: string; sample_files?: string[] }>
  }>>>({
    product: [],
    customer: []
  })
  const [loadingFolders, setLoadingFolders] = useState(false)
  const [savingConfig, setSavingConfig] = useState(false)
  const [folderSearchQuery, setFolderSearchQuery] = useState('')
  
  const [selectedFolders, setSelectedFolders] = useState<Record<string, Set<string>>>({
    product: new Set(),
    customer: new Set()
  })
  
  // Track configured folder names for display
  const [configuredFolders, setConfiguredFolders] = useState<{
    product?: { folder_id: string; folder_name: string }
    customer?: { folder_id: string; folder_name: string }
  }>({})
  
  // Manual upload state
  const [uploadedFiles, setUploadedFiles] = useState<{
    customer: Array<{ id: string; name: string; size: number; uploadedAt: Date; status: 'uploading' | 'success' | 'error'; errorMessage?: string }>
    product: Array<{ id: string; name: string; size: number; uploadedAt: Date; status: 'uploading' | 'success' | 'error'; errorMessage?: string }>
  }>({
    customer: [],
    product: []
  })
  
  // File validation constants
  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
  const ALLOWED_CUSTOMER_TYPES = ['.csv']
  const ALLOWED_PRODUCT_TYPES = ['.csv', '.docx', '.md', '.txt']
  const [uploading, setUploading] = useState<{ customer: boolean; product: boolean }>({
    customer: false,
    product: false
  })
  const fileInputRefs = {
    customer: React.useRef<HTMLInputElement>(null),
    product: React.useRef<HTMLInputElement>(null)
  }
  
  // Pipeline trigger state
  const [pipelineRunning, setPipelineRunning] = useState(false)
  const [pipelineStatus, setPipelineStatus] = useState<string | null>(null)
  
  // Expanded file lists
  const [expandedFiles, setExpandedFiles] = useState<{
    customer: boolean
    product: boolean
  }>({
    customer: false,
    product: false
  })
  
  // Load folders when modal opens
  useEffect(() => {
    if (showFolderConfig) {
      // Always load folders (will use mock data if not connected or API fails)
      loadFolders()
    }
  }, [showFolderConfig, folderConfigTab])
  
  // Load current config when connection status changes
  useEffect(() => {
    if (realConnectionStatus['google']?.connected) {
      loadCurrentConfig()
    } else {
      // Clear configured folders when disconnected
      setConfiguredFolders({})
      setSelectedFolders({ product: new Set(), customer: new Set() })
    }
  }, [realConnectionStatus['google']?.connected])
  
  // Also load config when component mounts (to show status even if modal hasn't been opened)
  useEffect(() => {
    if (realConnectionStatus['google']?.connected) {
      loadCurrentConfig()
    }
  }, []) // Only run on mount
  
  // Mock folder data for testing without backend
  const mockFoldersData = {
    product: [
      {
        folder_id: 'prod-1',
        folder_name: 'Product Specs',
        sample_files: ['Q1_2025_Roadmap.pdf', 'Feature_Requirements_v2.docx', 'API_Specification.md']
      },
      {
        folder_id: 'prod-2',
        folder_name: 'Roadmap',
        sample_files: ['2025_Roadmap.xlsx', 'Q2_Planning.pdf', 'Feature_Priorities.csv']
      },
      {
        folder_id: 'prod-3',
        folder_name: 'Feature Requests',
        sample_files: ['Customer_Feature_Requests.csv', 'Internal_Requests.xlsx', 'Prioritized_Backlog.pdf']
      },
      {
        folder_id: 'prod-4',
        folder_name: 'Documentation',
        sample_files: ['User_Guide_v3.pdf', 'API_Documentation.md', 'Integration_Guide.docx']
      }
    ],
    customer: [
      {
        folder_id: 'cust-1',
        folder_name: 'Customer Feedback',
        sample_files: ['QBR_Notes_Q1.csv', 'Support_Tickets_Export.xlsx', 'Customer_Survey_Results.pdf']
      },
      {
        folder_id: 'cust-2',
        folder_name: 'Account Health',
        sample_files: ['Health_Score_Dashboard.xlsx', 'Account_Reviews_Q1.pdf', 'Churn_Risk_Analysis.csv']
      },
      {
        folder_id: 'cust-3',
        folder_name: 'Engagement Logs',
        sample_files: ['Email_Logs_2025.csv', 'Meeting_Notes.xlsx', 'Call_Transcripts_Q1.pdf']
      },
      {
        folder_id: 'cust-4',
        folder_name: 'Sales Data',
        sample_files: ['Opportunities_Pipeline.csv', 'Deal_History.xlsx', 'Account_Contacts.pdf']
      }
    ]
  }

  const loadFolders = async () => {
    if (!user?.id) {
      // Use mock data if no user (for testing)
      setFolders(mockFoldersData)
      return
    }
    
    setLoadingFolders(true)
    try {
      const endpoint = folderConfigTab === 'product' 
        ? apiService.googleDrive.browseProductFolders
        : apiService.googleDrive.browseCustomerFolders
      
      const response = await endpoint(undefined, true)
      if (response.data.folders) {
        setFolders(prev => ({
          ...prev,
          [folderConfigTab]: response.data.folders.map((f: any) => ({
            folder_id: f.folder_id,
            folder_name: f.folder_name,
            sample_files: f.sample_files || []
          }))
        }))
      }
    } catch (error) {
      console.error('[Settings] Error loading folders, using mock data:', error)
      // Fall back to mock data on error
      setFolders(prev => ({
        ...prev,
        [folderConfigTab]: mockFoldersData[folderConfigTab]
      }))
      // Don't show error toast - just use mock data silently
    } finally {
      setLoadingFolders(false)
    }
  }
  
  const loadCurrentConfig = async () => {
    // Always check localStorage first (for mock mode or fallback)
    const savedConfig = localStorage.getItem('google_drive_config_mock')
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig)
        const newSelected: Record<string, Set<string>> = {
          product: new Set(),
          customer: new Set()
        }
        const newConfigured: typeof configuredFolders = {}
        
        if (config.product_insights_folder_id) {
          newSelected.product.add(config.product_insights_folder_id)
          newConfigured.product = {
            folder_id: config.product_insights_folder_id,
            folder_name: config.product_insights_folder_name || 'Unknown'
          }
        }
        if (config.customer_insights_folder_id) {
          newSelected.customer.add(config.customer_insights_folder_id)
          newConfigured.customer = {
            folder_id: config.customer_insights_folder_id,
            folder_name: config.customer_insights_folder_name || 'Unknown'
          }
        }
        
        setSelectedFolders(newSelected)
        setConfiguredFolders(newConfigured)
      } catch (e) {
        console.error('[Settings] Error parsing saved mock config:', e)
      }
    }
    
    // If user is logged in and connected, also try to load from API (will override localStorage if successful)
    if (user?.id && realConnectionStatus['google']?.connected) {
      try {
        const response = await apiService.googleDrive.getConfig()
        if (response.data.config) {
          const config = response.data.config
          const newSelected: Record<string, Set<string>> = {
            product: new Set(),
            customer: new Set()
          }
          const newConfigured: typeof configuredFolders = {}
          
          if (config.product_insights_folder_id) {
            newSelected.product.add(config.product_insights_folder_id)
            newConfigured.product = {
              folder_id: config.product_insights_folder_id,
              folder_name: config.product_insights_folder_name || 'Unknown'
            }
          }
          if (config.customer_insights_folder_id) {
            newSelected.customer.add(config.customer_insights_folder_id)
            newConfigured.customer = {
              folder_id: config.customer_insights_folder_id,
              folder_name: config.customer_insights_folder_name || 'Unknown'
            }
          }
          
          setSelectedFolders(newSelected)
          setConfiguredFolders(newConfigured)
        }
      } catch (error) {
        console.error('[Settings] Error loading current config from API, using localStorage:', error)
        // If API fails, we already loaded from localStorage above, so we're good
      }
    }
  }
  
  const saveFolderConfig = async () => {
    setSavingConfig(true)
    try {
      // Get selected folder IDs and names (can be multiple or none)
      const productFolderIds = Array.from(selectedFolders.product)
      const customerFolderIds = Array.from(selectedFolders.customer)
      
      // For MVP, we'll use the first selected folder for each type (or null if none selected)
      const productFolderId = productFolderIds[0] || null
      const customerFolderId = customerFolderIds[0] || null
      
      const productFolder = productFolderId ? folders.product.find(f => f.folder_id === productFolderId) : null
      const customerFolder = customerFolderId ? folders.customer.find(f => f.folder_id === customerFolderId) : null
      
      const configPayload: any = {}
      if (productFolderId && productFolder) {
        configPayload.product_insights_folder_id = productFolderId
        configPayload.product_insights_folder_name = productFolder.folder_name
      }
      if (customerFolderId && customerFolder) {
        configPayload.customer_insights_folder_id = customerFolderId
        configPayload.customer_insights_folder_name = customerFolder.folder_name
      }
      
      // Update configured folders display FIRST (so it shows immediately)
      const newConfigured: typeof configuredFolders = {}
      if (productFolderId && productFolder) {
        newConfigured.product = {
          folder_id: productFolderId,
          folder_name: productFolder.folder_name
        }
      }
      if (customerFolderId && customerFolder) {
        newConfigured.customer = {
          folder_id: customerFolderId,
          folder_name: customerFolder.folder_name
        }
      }
      setConfiguredFolders(newConfigured)
      
      // If user is not logged in or backend not connected, save to localStorage (mock save)
      // Persist selection locally (including nulls) so status + modal reflect latest choice immediately
      const configForLocalStorage = {
        product_insights_folder_id: productFolderId,
        product_insights_folder_name: productFolder?.folder_name || null,
        customer_insights_folder_id: customerFolderId,
        customer_insights_folder_name: customerFolder?.folder_name || null,
      }
      localStorage.setItem('google_drive_config_mock', JSON.stringify(configForLocalStorage))

      if (!user?.id || !realConnectionStatus['google']?.connected) {
        if (import.meta.env.DEV) {
          console.debug('[Settings] Mock save - no backend connection:', {
            product: productFolder?.folder_name || 'none',
            customer: customerFolder?.folder_name || 'none'
          })
        }
        setToast({
          id: Date.now(),
          message: 'Folder configuration saved (mock mode - backend not connected)'
        })
        setTimeout(() => setToast(null), 3000)
        setShowFolderConfig(false)
        setSavingConfig(false)
        return
      }
      
      // If both are unselected, we still save the config (with null values) to clear previous selections
      await apiService.googleDrive.saveConfig(configPayload)
      // Reload from API to stay in sync with backend
      await loadCurrentConfig()
      
      setToast({
        id: Date.now(),
        message: 'Folder configuration saved successfully!'
      })
      setTimeout(() => setToast(null), 3000)
      setShowFolderConfig(false)
    } catch (error) {
      console.error('[Settings] Error saving folder config:', error)
      setToast({
        id: Date.now(),
        message: 'Failed to save folder configuration. Please try again.'
      })
      setTimeout(() => setToast(null), 5000)
    } finally {
      setSavingConfig(false)
    }
  }

  // Mock discovery outputs per datasource for demo
  const [discoveryMockById] = useState<Record<string, { objects: { name: string; sample: string; includeDefault: boolean }[]; fieldsSuggested: number; filtersSuggested: number }>>({
    google: { objects: [
      { name: 'GmailMessage', sample: 'From: ceo@acme.com — Subject: Q1 KPI', includeDefault: true },
      { name: 'CalendarEvent', sample: 'Meeting: Value Review — 2025‑09‑20', includeDefault: true },
      { name: 'DriveFile', sample: 'OKR_Q1.xlsx — /Finance/Reports', includeDefault: false },
    ], fieldsSuggested: 24, filtersSuggested: 6 },
    notion: { objects: [
      { name: 'Page', sample: 'Spec: Alerts v2', includeDefault: true },
      { name: 'Database', sample: 'Roadmap 2025', includeDefault: true },
    ], fieldsSuggested: 14, filtersSuggested: 3 },
    salesforce: { objects: [
      { name: 'Account', sample: 'Acme Corp — ARR 150k', includeDefault: true },
      { name: 'Opportunity', sample: 'Q4 Renewal — Stage: Negotiation', includeDefault: true },
      { name: 'Contact', sample: 'Sarah Chen — PM', includeDefault: true },
    ], fieldsSuggested: 31, filtersSuggested: 4 },
  })
  
  // Check URL params for OAuth success/error redirects
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const oauthSuccess = params.get('oauth_success')
    const oauthError = params.get('oauth_error')
    const errorMessage = oauthError ? params.get('error') : null
    
    // Handle OAuth success
    if (oauthSuccess) {
      const providerName = oauthSuccess.charAt(0).toUpperCase() + oauthSuccess.slice(1)
      setToast({
        id: Date.now(),
        message: `${providerName} connected successfully! You can now explore and configure folders for insights.`
      })
      
      // Auto-dismiss toast after 5 seconds
      setTimeout(() => setToast(null), 5000)
      
      // Clear URL params immediately
      window.history.replaceState({}, '', '/setup')
      
      // Refresh connection status to show updated state
      // Don't wait for user?.id - use session cookie for auth
      const refreshStatus = async () => {
        try {
          const response = await fetch(`${config.apiBaseUrl}/api/oauth/status`, { 
            credentials: 'include' 
          })
          
          if (response.ok) {
            const data = await response.json()
            if (data.success && data.datasources) {
              const statusMap: Record<string, any> = {}
              data.datasources.forEach((ds: any) => {
                statusMap[ds.id] = ds
              })
              if (import.meta.env.DEV) {
                console.debug('[Settings] Updated OAuth status after connection:', statusMap)
              }
              setRealConnectionStatus(statusMap)
            }
          } else {
            if (import.meta.env.DEV) {
              console.error('[Settings] Failed to refresh OAuth status:', response.status)
            }
            // Retry after a short delay if session might not be ready yet
            if (response.status === 401) {
              setTimeout(refreshStatus, 1000)
            }
          }
        } catch (err) {
          console.error('[Settings] Error refreshing OAuth status:', err)
          // Retry after a short delay
          setTimeout(refreshStatus, 1000)
      }
      }
      
      // Try immediately, and retry once after 1 second in case session isn't ready
      refreshStatus()
      setTimeout(refreshStatus, 1000)
    }
    
    // Handle OAuth error
    if (oauthError) {
      const providerName = oauthError.charAt(0).toUpperCase() + oauthError.slice(1)
      setToast({
        id: Date.now(),
        message: `Failed to connect ${providerName}. ${errorMessage || 'Please try again.'}`
      })
      
      // Auto-dismiss toast after 5 seconds
      setTimeout(() => setToast(null), 5000)
      
      // Clear URL params
      window.history.replaceState({}, '', '/setup')
    }
  }, []) // Run once on mount, don't depend on user
  
  // 🆕 Fetch real OAuth status and discovery progress
  useEffect(() => {
    // Wait for auth to finish loading before fetching
    if (authLoading) {
      if (import.meta.env.DEV) {
        console.debug('[Settings] Auth still loading, waiting...')
      }
      return
    }
    
    if (!user?.id) {
      if (import.meta.env.DEV) {
        console.debug('[Settings] No user.id, skipping status fetch')
      }
      // Clear status if user logs out
      setRealConnectionStatus({})
      return
    }
    
    const fetchStatus = async () => {
      try {
        // Use session-based auth - no need to pass user_id, backend gets it from session
        // This prevents issues with stale user_id in closures or query parameters
        const response = await fetch(
          `${config.apiBaseUrl}/api/oauth/status`,
          { credentials: 'include' }
        )
        
        if (response.ok) {
          const data = await response.json()
          
          // Convert array to map: { salesforce: {...}, google: {...} }
          const statusMap: Record<string, any> = {}
          data.datasources?.forEach((ds: any) => {
            statusMap[ds.id] = ds
          })
          
          setRealConnectionStatus(statusMap)
          
          // Update job states based on connection status
          // Note: Discovery module is from backend_v2 (legacy) and not used in current MVP
          const jobStates: Record<string, JobState> = {}
          data.datasources?.forEach((ds: any) => {
            // Simple logic: if connected, show as idle (ready to use), if not connected, show as idle (not ready)
            // The pipeline will handle actual processing status
            if (ds.connected) {
              jobStates[ds.id] = 'idle' // Connected and ready to use
            } else {
              jobStates[ds.id] = 'idle' // Not connected, idle state
            }
          })
          
          setJobStateById(prev => ({ ...prev, ...jobStates }))
        } else if (response.status === 401) {
          // Session expired or invalid - clear status
          if (import.meta.env.DEV) {
            console.warn('[Settings] OAuth status fetch returned 401 - session expired')
          }
          setRealConnectionStatus({})
        }
      } catch (error) {
        console.error('Error fetching OAuth status:', error)
      }
    }
    
    fetchStatus()
    
    // Poll every 5 seconds for discovery progress
    const interval = setInterval(fetchStatus, 5000)
    return () => clearInterval(interval)
  }, [user, authLoading])
  
  // Seed some initial states for demo (fallback if no real data)
  useEffect(() => {
    // Only set mock states if no real data loaded yet AND user is not logged in
    if (Object.keys(realConnectionStatus).length === 0 && !user?.id) {
      setJobStateById(prev => ({
        ...prev,
        google: 'needs_review',
        notion: 'active',
        salesforce: 'needs_review',
      }))
    }
  }, [realConnectionStatus, user])

  // Per-column filters
  const [nameFilter, setNameFilter] = useState<string>('')
  const [productFilter, setProductFilter] = useState<DomainStatus | 'any'>('any')
  const [customerFilter, setCustomerFilter] = useState<DomainStatus | 'any'>('any')
  const [pipelineFilter, setPipelineFilter] = useState<'any' | 'healthy' | 'degraded' | 'failing'>('any')
  const [openFilter, setOpenFilter] = useState<null | 'product' | 'customer' | 'status'>(null)
  const [openNameFilter, setOpenNameFilter] = useState<boolean>(false)
  // removed per-column sort menu for Datasource; using standard header toggle
  const [statusFilter, setStatusFilter] = useState<'any' | JobState>('any')

  // Sorting
  type SortCol = 'name' | 'product' | 'customer' | 'status'
  const [sortBy, setSortBy] = useState<SortCol>('status')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const toggleSort = (col: SortCol) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(col)
      setSortDir('asc')
    }
  }

  // 🆕 Merge mock datasources with real connection status
  const datasourcesWithRealStatus = useMemo((): Datasource[] => {
    return mockDatasources.map(ds => {
      const realStatus = realConnectionStatus[ds.id]
      if (!realStatus) return ds
      
      // Update connection status based on real OAuth data
      const connected = realStatus.connected
      
      // If connected, set status to 'connected', otherwise 'not_connected' (don't use mock status)
      const customerConnected: DomainStatus = ds.supports.customer 
        ? (connected ? 'connected' : 'not_connected')
        : 'not_supported'
      const productConnected: DomainStatus = ds.supports.product 
        ? (connected ? 'connected' : 'not_connected')
        : 'not_supported'
      
      // Note: Discovery module is from backend_v2 (legacy) and not used in current MVP
      // Pipeline health will be determined by actual pipeline runs, not discovery status
      return {
        ...ds,
        status: {
          product: productConnected,
          customer: customerConnected
        },
        // Keep existing lastSyncTs and pipelineHealth from mock datasources
        // These will be updated by actual pipeline runs
        lastSyncTs: ds.lastSyncTs,
        pipelineHealth: ds.pipelineHealth,
        lastError: ds.lastError
      }
    })
  }, [realConnectionStatus])

  const rows = useMemo(() => {
    // base filter by top-level filter dropdown
    let list = datasourcesWithRealStatus.filter(ds => {
      if (filter === 'all') return true
      if (filter === 'product') return ds.supports.product
      if (filter === 'customer') return ds.supports.customer
      if (filter === 'connected') return ds.status.product === 'connected' || ds.status.customer === 'connected'
      if (filter === 'issues') return ds.status.product === 'issues' || ds.status.customer === 'issues'
      if (filter === 'needs_setup') return (ds.status.product === 'not_connected' && ds.status.customer === 'not_connected')
      return true
    })

    // column filters
    if (nameFilter.trim()) {
      const q = nameFilter.trim().toLowerCase()
      list = list.filter(ds => ds.name.toLowerCase().includes(q))
    }
    if (productFilter !== 'any') {
      list = list.filter(ds => (ds.supports.product ? ds.status.product : 'not_supported') === productFilter)
    }
    if (customerFilter !== 'any') {
      list = list.filter(ds => (ds.supports.customer ? ds.status.customer : 'not_supported') === customerFilter)
    }
    if (pipelineFilter !== 'any') {
      list = list.filter(ds => ds.pipelineHealth === pipelineFilter)
    }
    // Status filter (UI-only, based on jobState)
    if ((statusFilter as any) && statusFilter !== 'any') {
      list = list.filter(ds => (jobStateById[ds.id] || 'idle') === statusFilter)
    }

    const lastSyncValue = (ts?: string) => (ts ? new Date(ts).getTime() : 0)

    // apply sort strictly by selected column
    const compare = (a: typeof mockDatasources[number], b: typeof mockDatasources[number]) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name)
      } else if (sortBy === 'product') {
        const order: Record<DomainStatus, number> = { issues: 0, not_connected: 1, connected: 2, coming_soon: 3, not_supported: 4 }
        const av = order[a.supports.product ? a.status.product : 'not_supported']
        const bv = order[b.supports.product ? b.status.product : 'not_supported']
        return av - bv
      } else if (sortBy === 'customer') {
        const order: Record<DomainStatus, number> = { issues: 0, not_connected: 1, connected: 2, coming_soon: 3, not_supported: 4 }
        const av = order[a.supports.customer ? a.status.customer : 'not_supported']
        const bv = order[b.supports.customer ? b.status.customer : 'not_supported']
        return av - bv
      } else if (sortBy === 'status') {
        const order: Record<JobState, number> = {
          needs_review: 0,
          preparing: 1,
          active: 2,
          paused: 3,
          discovery_running: 4,
          attention: 5,
          idle: 6,
        }
        const av = order[(jobStateById[a.id] || 'idle')]
        const bv = order[(jobStateById[b.id] || 'idle')]
        return av - bv
      } else {
        return 0
      }
    }

    list = [...list].sort((a, b) => {
      const c = compare(a, b)
      return sortDir === 'asc' ? c : -c
    })

    return list
  }, [datasourcesWithRealStatus, filter, nameFilter, productFilter, customerFilter, pipelineFilter, sortBy, sortDir])

  const isSupported = (ds: Datasource) => ds.supports.product || ds.supports.customer
  const isConnected = (ds: Datasource) => ds.status.product === 'connected' || ds.status.customer === 'connected'
  const needsConnect = (ds: Datasource) => isSupported(ds) && !isConnected(ds)

  const onConnect = (ds: Datasource) => {
    // 🔗 Real OAuth integration
    // Redirect to backend OAuth endpoint which will:
    // 1. Initiate OAuth with provider
    // 2. Auto-trigger discovery after success
    // 3. Redirect to /discovery-qa page with results
    
    // Ensure user is logged in
    if (!user?.id) {
      navigate('/login')
      return
    }
    
    // Redirect to OAuth connection endpoint with real user ID
    // Use direct backend URL for OAuth (not proxied) since this is a full page redirect
    const backendUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';
    window.location.href = `${backendUrl}/api/oauth/connect/${ds.id}?user_id=${user.id}`;
  }
  const onEdit = (ds: Datasource) => {
    setExpandedId(expandedId === ds.id ? null : ds.id)
    setDetailsTab('summary')
  }
  const onSync = (ds: Datasource) => {
    alert(`Sync started for ${ds.name} (mock)`) 
  }

  const onConfirmAndContinue = (ds: Datasource) => {
    setJobStateById(prev => ({ ...prev, [ds.id]: 'preparing' }))
    setToast({ id: Date.now(), message: `Preparing insights for ${ds.name}` })
    setTimeout(() => setToast(null), 2500)
    setTimeout(() => setJobStateById(prev => ({ ...prev, [ds.id]: 'active' })), 3000)
  }

  const onPauseResume = (ds: Datasource) => {
    const state = jobStateById[ds.id]
    const next: JobState = state === 'paused' ? 'active' : 'paused'
    setJobStateById(prev => ({ ...prev, [ds.id]: next }))
  }

  const primaryLabel = (ds: Datasource) => (needsConnect(ds) ? 'Connect' : 'Edit')

  // Legacy discovery function - removed because discovery module is from backend_v2 (not part of current MVP)
  // The pipeline handles data processing directly without a separate discovery phase
  const onRefreshDiscovery = async (ds: Datasource) => {
    console.warn('[Settings] Discovery refresh is not available in MVP - discovery module is from backend_v2 (legacy)')
    // No-op: Discovery is not part of the current MVP architecture
  }
  
  const handleFileUpload = async (domain: 'customer' | 'product', files: FileList | null) => {
    console.log(`[Settings] handleFileUpload called - domain: ${domain}`)
    console.log(`[Settings] files parameter:`, files)
    console.log(`[Settings] files type:`, typeof files)
    console.log(`[Settings] files instanceof FileList:`, files instanceof FileList)
    console.log(`[Settings] files?.length:`, files?.length)
    
    if (!files) {
      console.warn('[Settings] No files provided to handleFileUpload (files is null)')
      return
    }
    
    // Convert FileList to array IMMEDIATELY before doing anything else
    // This is critical because resetting the input will clear the FileList
    const filesArray = Array.from(files)
    console.log(`[Settings] Files array after conversion:`, filesArray)
    console.log(`[Settings] Files array length:`, filesArray.length)
    console.log(`[Settings] File names:`, filesArray.map(f => f.name))
    
    if (filesArray.length === 0) {
      console.warn('[Settings] No files in FileList after conversion')
      return
    }
    
    // NOW reset file input (after we've captured the files in an array)
    if (domain === 'customer') {
      fileInputRefs.customer.current!.value = ''
    } else {
      fileInputRefs.product.current!.value = ''
    }
    
    // Validate files before processing
    const allowedTypes = domain === 'customer' ? ALLOWED_CUSTOMER_TYPES : ALLOWED_PRODUCT_TYPES
    const invalidFiles: Array<{ file: File; error: string }> = []
    const validFiles: File[] = []
    
    console.log(`[Settings] Starting validation - domain: ${domain}, files array length: ${filesArray.length}`)
    console.log(`[Settings] Allowed types for ${domain}:`, allowedTypes)
    
    filesArray.forEach((file, index) => {
      console.log(`[Settings] ===== Processing file ${index + 1}/${filesArray.length} =====`)
      console.log(`[Settings] File name: "${file.name}"`)
      console.log(`[Settings] File type: "${file.type}"`)
      console.log(`[Settings] File size: ${file.size} bytes`)
      
      // Check file extension
      const parts = file.name.split('.')
      console.log(`[Settings] File name parts after split('.'):`, parts)
      const lastPart = parts.pop()?.toLowerCase()
      console.log(`[Settings] Last part (extension): "${lastPart}"`)
      const fileExtension = lastPart ? '.' + lastPart : ''
      console.log(`[Settings] File extension extracted: "${fileExtension}"`)
      console.log(`[Settings] Allowed types:`, allowedTypes)
      console.log(`[Settings] Extension in allowed types?`, allowedTypes.includes(fileExtension))
      
      if (!allowedTypes.includes(fileExtension)) {
        console.error(`[Settings] ❌ File ${file.name} REJECTED: extension "${fileExtension}" not in allowed types ${JSON.stringify(allowedTypes)}`)
        invalidFiles.push({
          file,
          error: `File type not supported. Allowed types: ${allowedTypes.join(', ')}`
        })
        return
      }
      
      // Check file size
      console.log(`[Settings] File size check: ${file.size} bytes vs max ${MAX_FILE_SIZE} bytes`)
      if (file.size > MAX_FILE_SIZE) {
        console.error(`[Settings] ❌ File ${file.name} REJECTED: size ${file.size} exceeds limit ${MAX_FILE_SIZE}`)
        invalidFiles.push({
          file,
          error: `File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`
        })
        return
      }
      
      console.log(`[Settings] ✅ File ${file.name} PASSED validation`)
      validFiles.push(file)
      console.log(`[Settings] Valid files count now: ${validFiles.length}`)
    })
    
    console.log(`[Settings] ===== Validation complete =====`)
    console.log(`[Settings] Valid files: ${validFiles.length}, Invalid files: ${invalidFiles.length}`)
    
    // Show error messages for invalid files
    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles.map(({ file, error }) => `${file.name}: ${error}`).join('; ')
      setToast({
        id: Date.now(),
        message: `${invalidFiles.length} file(s) rejected: ${errorMessages}`
      })
      setTimeout(() => setToast(null), 7000)
    }
    
    // If no valid files, return early
    if (validFiles.length === 0) {
      console.warn(`[Settings] No valid files after validation - domain: ${domain}, invalid: ${invalidFiles.length}`)
      return
    }
    
    console.log(`[Settings] Starting upload - domain: ${domain}, validFiles: ${validFiles.length}, user: ${user?.id}`)
    setUploading(prev => ({ ...prev, [domain]: true }))
    
    // Add invalid files to state with 'error' status
    const invalidFileEntries = invalidFiles.map(({ file, error }, idx) => ({
      id: `${domain}-invalid-${Date.now()}-${idx}`,
      name: file.name,
      size: file.size,
      uploadedAt: new Date(),
      status: 'error' as const,
      errorMessage: error
    }))
    
    // Add valid files to state with 'uploading' status
    const newFiles = validFiles.map((file, idx) => ({
      id: `${domain}-${Date.now()}-${idx}`,
      name: file.name,
      size: file.size,
      uploadedAt: new Date(),
      status: 'uploading' as const
    }))
    
    setUploadedFiles(prev => ({
      ...prev,
      [domain]: [...prev[domain], ...invalidFileEntries, ...newFiles]
    }))
    
    // Mock mode: Only use if no user is logged in
    // In production, always use real upload if user is logged in
    const useMock = !user?.id
    
    console.log(`[Settings] Upload mode: ${useMock ? 'MOCK (no user logged in)' : 'REAL (user logged in)'}`)
    console.log(`[Settings] User object:`, user)
    
    if (useMock) {
      console.log('[Settings] Using mock upload mode - user not logged in')
      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // Randomly set some to success, some to error for testing
      const mockResults = newFiles.map((file, idx) => ({
        ...file,
        status: (idx % 3 === 0 ? 'error' : 'success') as 'success' | 'error',
        errorMessage: idx % 3 === 0 ? 'Upload failed. Please try again.' : undefined
      }))
      
      setUploadedFiles(prev => ({
        ...prev,
        [domain]: prev[domain].map(f => {
          const mockResult = mockResults.find(mr => mr.id === f.id)
          return mockResult || f
        })
      }))
      
      const successCount = mockResults.filter(r => r.status === 'success').length
      const errorCount = mockResults.filter(r => r.status === 'error').length
      
      if (errorCount > 0) {
        setToast({
          id: Date.now(),
          message: `Uploaded ${successCount} file(s) successfully, ${errorCount} failed. (Mock mode)`
        })
      } else {
        setToast({
          id: Date.now(),
          message: `Successfully uploaded ${successCount} file(s) for ${domain === 'customer' ? 'Customer' : 'Product'} Insights. (Mock mode)`
        })
      }
      setTimeout(() => setToast(null), 5000)
      setUploading(prev => ({ ...prev, [domain]: false }))
      return
    }
    
    // Real upload mode
    try {
      if (domain === 'customer') {
        // Customer Insights: Use async endpoint with multiple file support
        const formData = new FormData()
        Array.from(validFiles).forEach((file) => {
          formData.append('files[]', file)
          console.log(`[Settings] Added file to FormData: ${file.name} (${file.size} bytes)`)
        })
        formData.append('account_id', 'csv_upload') // Default account for manual uploads
        formData.append('domain', 'customer_insights')
        
        const uploadUrl = `${config.apiBaseUrl}/api/customer-insights/upload/csv/async`
        console.log(`[Settings] Making POST request to: ${uploadUrl}`)
        
        // Log FormData entries (check if value is a File by checking for name property)
        try {
          const entries = Array.from(formData.entries()).map(([k, v]) => {
            if (v && typeof v === 'object' && 'name' in v && 'size' in v) {
              return [k, `${(v as File).name} (${(v as File).size} bytes)`]
            }
            return [k, String(v)]
          })
          console.log(`[Settings] FormData entries:`, entries)
        } catch (e) {
          console.log(`[Settings] Could not log FormData entries:`, e)
        }
        
        const response = await fetch(uploadUrl, {
          method: 'POST',
          credentials: 'include',
          body: formData
        })
        
        console.log(`[Settings] Upload response received:`, response.status, response.statusText, response.ok)
        
        if (!response.ok) {
          let errorData
          try {
            const text = await response.text()
            try {
              errorData = JSON.parse(text)
            } catch {
              errorData = { error: text || `Upload failed: ${response.status} ${response.statusText}` }
            }
          } catch (e) {
            errorData = { error: `Upload failed: ${response.status} ${response.statusText}` }
          }
          throw new Error(errorData.error || `Upload failed: ${response.status} ${response.statusText}`)
        }
        
        const result = await response.json()
        if (import.meta.env.DEV) {
          console.debug('[Settings] Customer Insights upload result:', result)
        }
        
        // Update file statuses to success
        setUploadedFiles(prev => ({
          ...prev,
          customer: prev.customer.map(f => 
            newFiles.some(nf => nf.id === f.id) ? { ...f, status: 'success' as const } : f
          )
        }))
        
        setToast({
          id: Date.now(),
          message: `Successfully uploaded ${validFiles.length} file(s) for Customer Insights. Processing in background.`
        })
        setTimeout(() => setToast(null), 5000)
      } else {
        // Product Insights: Use async endpoint with multiple file support
        const formData = new FormData()
        Array.from(validFiles).forEach((file) => {
          formData.append('files[]', file)
        })
        
        if (import.meta.env.DEV) {
          console.debug(`[Settings] Uploading ${validFiles.length} file(s) for Product Insights`)
        }
        
        const response = await fetch(`${config.apiBaseUrl}/api/product-insights/uploads/async`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        })
        
        if (!response.ok) {
          const errorText = await response.text()
          let errorData
          try {
            errorData = JSON.parse(errorText)
          } catch {
            errorData = { error: errorText || 'Upload failed' }
          }
          throw new Error(errorData.error || `Upload failed: ${response.status} ${response.statusText}`)
        }
        
        const result = await response.json()
        if (import.meta.env.DEV) {
          console.debug('[Settings] Product Insights upload result:', result)
        }
        
        // Update file statuses to success
        setUploadedFiles(prev => ({
          ...prev,
          product: prev.product.map(f => 
            newFiles.some(nf => nf.id === f.id) ? { ...f, status: 'success' as const } : f
          )
        }))
        
        setToast({
          id: Date.now(),
          message: `Successfully uploaded ${validFiles.length} file(s) for Product Insights. Processing in background...`
        })
        setTimeout(() => setToast(null), 5000)
      }
    } catch (error) {
      // Log detailed error information
      const errorMessage = error instanceof Error ? error.message : 'Upload failed. Please try again.'
      console.error(`[Settings] Error uploading ${domain} files:`, {
        error,
        errorMessage,
        domain,
        fileCount: validFiles.length,
        fileNames: validFiles.map(f => f.name)
      })
      
      // Update file statuses to error
      setUploadedFiles(prev => ({
        ...prev,
        [domain]: prev[domain].map(f => 
          newFiles.some(nf => nf.id === f.id) ? { ...f, status: 'error' as const, errorMessage } : f
        )
      }))
      
      setToast({
        id: Date.now(),
        message: `Failed to upload files. ${errorMessage}`
      })
      setTimeout(() => setToast(null), 7000)
    } finally {
      setUploading(prev => ({ ...prev, [domain]: false }))
    }
  }
  
  // Load mock uploaded files on mount (for testing)
  useEffect(() => {
    if (import.meta.env.DEV) {
      // Add some sample files for testing
      const mockCustomerFiles = [
        { id: 'mock-ci-1', name: 'QBR_Notes_Q1_2025.csv', size: 245760, uploadedAt: new Date(Date.now() - 2 * 3600000), status: 'success' as const },
        { id: 'mock-ci-2', name: 'Support_Tickets_Export.xlsx', size: 512000, uploadedAt: new Date(Date.now() - 5 * 3600000), status: 'success' as const },
        { id: 'mock-ci-3', name: 'Customer_Survey_Results.pdf', size: 1024000, uploadedAt: new Date(Date.now() - 24 * 3600000), status: 'success' as const },
      ]
      const mockProductFiles = [
        { id: 'mock-pi-1', name: 'Q1_2025_Roadmap.pdf', size: 1536000, uploadedAt: new Date(Date.now() - 1 * 3600000), status: 'success' as const },
        { id: 'mock-pi-2', name: 'Feature_Requirements_v2.docx', size: 768000, uploadedAt: new Date(Date.now() - 3 * 3600000), status: 'success' as const },
        { id: 'mock-pi-3', name: 'API_Specification.md', size: 128000, uploadedAt: new Date(Date.now() - 12 * 3600000), status: 'success' as const },
      ]
      
      setUploadedFiles({
        customer: mockCustomerFiles,
        product: mockProductFiles
      })
    }
  }, [])
  
  // Check if pipeline can be triggered (has data sources)
  // For Google Drive: Must be connected AND have at least 1 folder configured (product or customer)
  // For Manual Upload: Must have at least 1 file uploaded (product or customer)
  const canTriggerPipeline = useMemo(() => {
    const hasGoogleDrive = realConnectionStatus['google']?.connected && 
                          (configuredFolders.product || configuredFolders.customer)
    const hasManualUploads = uploadedFiles.customer.some(f => f.status === 'success') || 
                             uploadedFiles.product.some(f => f.status === 'success')
    return hasGoogleDrive || hasManualUploads
  }, [realConnectionStatus, configuredFolders, uploadedFiles])
  
  // Trigger pipeline
  const handleCreateInsights = async () => {
    if (!user?.id) {
      setToast({
        id: Date.now(),
        message: 'Please log in to create insights'
      })
      setTimeout(() => setToast(null), 5000)
      return
    }
    
    setPipelineRunning(true)
    setPipelineStatus('Starting pipeline...')
    
    try {
      // Smart detection: Determine which sources and domains to process
      const sources: string[] = []
      const domains: string[] = []
      
      // Check Google Drive connection
      const hasGoogleDrive = realConnectionStatus['google']?.connected
      if (hasGoogleDrive) {
        sources.push('google_drive')
        // Google Drive can process both domains if folders are configured
        if (configuredFolders.customer || configuredFolders.product) {
          if (configuredFolders.customer) domains.push('customer_insights')
          if (configuredFolders.product) domains.push('product_insights')
        } else {
          // If no folders configured, still process both (backend will use default)
          domains.push('customer_insights', 'product_insights')
        }
      }
      
      // Check manual uploads
      const hasCustomerUploads = uploadedFiles.customer.some(f => f.status === 'success')
      const hasProductUploads = uploadedFiles.product.some(f => f.status === 'success')
      
      if (hasCustomerUploads) {
        if (!sources.includes('manual_upload')) sources.push('manual_upload')
        if (!domains.includes('customer_insights')) domains.push('customer_insights')
      }
      
      if (hasProductUploads) {
        if (!sources.includes('manual_upload')) sources.push('manual_upload')
        if (!domains.includes('product_insights')) domains.push('product_insights')
      }
      
      // If no sources detected, show error
      if (sources.length === 0) {
        throw new Error('No data sources available. Please connect Google Drive or upload files.')
      }
      
      if (import.meta.env.DEV) {
        console.debug('[Settings] Triggering pipeline with:', { sources, domains })
      }
      
      const response = await apiService.sync.trigger({
        sources: sources.length > 0 ? sources : undefined,
        domains: domains.length > 0 ? domains : undefined,
        async: true, // Run asynchronously
        force: false
      })
      
      if (response.data.job_id) {
        setPipelineStatus('Pipeline queued successfully')
        setToast({
          id: Date.now(),
          message: 'Pipeline started! Processing your data in the background.'
        })
        setTimeout(() => setToast(null), 5000)
        
        // Poll for status (simplified - in production, use WebSocket or better polling)
        // For now, just show success after a delay
        setTimeout(() => {
          setPipelineRunning(false)
          setPipelineStatus('Completed')
        }, 3000)
      } else {
        throw new Error('No job ID returned')
      }
    } catch (error: any) {
      console.error('[Settings] Error triggering pipeline:', error)
      setPipelineRunning(false)
      setPipelineStatus(null)
      
      // Handle rate limiting with better messaging
      if (error.response?.status === 429) {
        const rateLimitMessage = error.response?.data?.message || 
                                error.response?.data?.error || 
                                'Rate limit exceeded. Please wait before trying again.'
        setToast({
          id: Date.now(),
          message: rateLimitMessage
        })
        setTimeout(() => setToast(null), 10000) // Show longer for rate limit errors
      } else {
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.error || 
                            error.message || 
                            'Failed to start pipeline'
      setToast({
        id: Date.now(),
        message: `Failed to start pipeline: ${errorMessage}`
      })
      setTimeout(() => setToast(null), 7000)
      }
    }
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
      {toast && (
        <div className={`fixed top-4 right-4 text-sm px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 ${
          toast.message.toLowerCase().includes('failed') || toast.message.toLowerCase().includes('error')
            ? 'bg-red-50 border border-red-200 text-red-800'
            : 'bg-green-50 border border-green-200 text-green-800'
        }`}>
          {toast.message.toLowerCase().includes('failed') || toast.message.toLowerCase().includes('error') ? (
            <XCircle className="w-5 h-5 text-red-600" />
          ) : (
            <CheckCircle className="w-5 h-5 text-green-600" />
          )}
          <span>{toast.message}</span>
        </div>
      )}
      
      {/* Success Banner: OAuth + Discovery Progress */}
      {successBanner && (
        <div className={`shrink-0 ${successBanner.status === 'completed' ? 'bg-green-50 border-b border-green-200' : 'bg-blue-50 border-b border-blue-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {successBanner.status === 'analyzing' ? (
                  <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                {successBanner.status === 'analyzing' ? (
                  <>
                    <p className="text-sm font-medium text-blue-900">
                      {successBanner.datasource} connected successfully!
                    </p>
                    <p className="text-sm text-blue-700 mt-0.5">
                      Analyzing your data to surface insights... This usually takes 20-30 seconds.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-green-900">
                      {successBanner.datasource} analysis complete!
                    </p>
                    <p className="text-sm text-green-700 mt-0.5">
                      We've found insights in your {successBanner.datasource} data. Ready to explore?
                    </p>
                  </>
                )}
              </div>
              
              {successBanner.status === 'completed' ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setSuccessBanner(null)
                      navigate('/customer-insights')
                    }}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
                  >
                    View Insights
                  </button>
                  <button
                    onClick={() => setSuccessBanner(null)}
                    className="px-4 py-2 bg-white text-green-700 text-sm font-medium rounded-md border border-green-300 hover:bg-green-50"
                  >
                    Stay Here
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setSuccessBanner(null)}
                  className="flex-shrink-0 text-blue-600 hover:text-blue-800"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="shrink-0">
        <PageHeader title="Setup">
          <button
            onClick={() => setActiveTab('integrations')}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium ${
              activeTab === 'integrations' ? 'bg-tandem-blue text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            Integrations
          </button>
          {/* MVP: Commented out for MVP pilot */}
          {false && (
            <>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium ${
                  activeTab === 'notifications' ? 'bg-tandem-blue text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Notifications
              </button>
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium ${
                  activeTab === 'dashboard' ? 'bg-tandem-blue text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Dashboard
              </button>
            </>
          )}
        </PageHeader>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-6">
        {activeTab === 'integrations' && (
          <>
            {/* MVP: Card-based Integration Grid */}
            <div className="mb-6">
              <div className="mb-2">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Data Sources</h2>
                <p className="text-sm text-gray-600">Connect your data sources to generate customer and product insights.</p>
              </div>
              
              {/* Helper banner with Create Insights button */}
              {canTriggerPipeline && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900 mb-0.5">Ready to create insights</p>
                      <p className="text-xs text-blue-700">
                        {(() => {
                          const hasGoogleDriveReady = realConnectionStatus['google']?.connected && 
                                                      (configuredFolders.product || configuredFolders.customer)
                          const hasManualUploads = uploadedFiles.customer.some(f => f.status === 'success') || 
                                                   uploadedFiles.product.some(f => f.status === 'success')
                          
                          if (hasGoogleDriveReady && hasManualUploads) {
                            return 'You have Google Drive configured and files uploaded. Process your data to generate insights.'
                          } else if (hasGoogleDriveReady) {
                            return 'Google Drive is configured with at least one folder. Process your data to generate insights.'
                          } else if (hasManualUploads) {
                            return 'Files are uploaded. Process your data to generate insights.'
                          }
                          return 'Ready to process your data.'
                        })()}
                      </p>
                      {pipelineStatus && (
                        <p className="text-xs text-blue-600 mt-1.5 font-medium">{pipelineStatus}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <Button
                        variant="primary"
                        onClick={handleCreateInsights}
                        disabled={pipelineRunning}
                        className="relative shadow-lg hover:shadow-xl transition-all"
                      >
                        {pipelineRunning ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <span>Create Insights</span>
                            <span className="ml-2 text-xs opacity-90">→</span>
                          </>
                        )}
                      </Button>
                      {/* Pulse animation indicator */}
                      {!pipelineRunning && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Google Drive Card */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                    <img src="/images/google.svg" alt="Google Drive" className="w-8 h-8" onError={(e: any) => { 
                      e.currentTarget.style.display = 'none'
                      const parent = e.currentTarget.parentElement
                      if (parent) parent.innerHTML = '<div class="text-blue-600 text-2xl font-bold">G</div>'
                    }} />
                  </div>
                  {realConnectionStatus['google']?.connected && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                      <CheckCircle className="w-3 h-3" />
                      Connected
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Google Drive</h3>
                <p className="text-sm text-gray-600 mb-4">Automatically sync customer and product insights from shared folders.</p>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Auto-sync</span>
                </div>
                {/* 
                  Google Drive OAuth Flow:
                  1. User clicks "Connect" button → calls apiService.oauth.authorizeGoogle()
                  2. Frontend redirects to /api/oauth/google (or /api/oauth/connect/google?user_id=xxx)
                  3. Backend receives request, initiates OAuth flow with Google:
                     - Builds authorization URL with scopes (Drive readonly, Gmail readonly)
                     - Stores OAuth state in session/Redis
                     - Redirects user to Google's authorization page
                  4. User authorizes on Google → Google redirects to /api/oauth/callback/google?code=xxx&state=xxx
                  5. Backend handles callback:
                     - Validates state parameter
                     - Exchanges authorization code for access/refresh tokens
                     - Stores tokens securely (Redis/database) associated with user_id
                     - Redirects back to /setup?oauth_success=google (or oauth_error=google)
                  6. Settings page displays success toast and refreshes connection status
                  7. User can now explore and configure folders for Product Insights and Customer Insights
                  
                  TESTING: To mock/test the redirect flow, you can navigate directly to:
                  - Success: /setup?oauth_success=google
                  - Error: /setup?oauth_error=google&error=Access%20denied
                */}
                <div className="flex items-center gap-2">
                  <Button 
                    variant={realConnectionStatus['google']?.connected ? 'secondary' : 'primary'} 
                    className="flex-1"
                    onClick={async () => {
                      if (realConnectionStatus['google']?.connected) {
                        // "Disconnect" button - disconnect OAuth
                        try {
                          if (user?.id) {
                            // Try real disconnect
                            try {
                              await apiService.googleDrive.disconnect()
                              // Refresh connection status
                              const response = await fetch(`${config.apiBaseUrl}/api/oauth/status`, { credentials: 'include' })
                              if (response.ok) {
                                const data = await response.json()
                                const statusMap: Record<string, any> = {}
                                data.datasources?.forEach((ds: any) => {
                                  statusMap[ds.id] = ds
                                })
                                setRealConnectionStatus(statusMap)
                              }
                            } catch (error) {
                              console.error('[Settings] Error disconnecting (real):', error)
                              // Fall through to mock disconnect
                            }
                          }
                          
                          // Mock disconnect (always works, even if real API fails)
                          setRealConnectionStatus(prev => ({
                            ...prev,
                            google: {
                              id: 'google',
                              connected: false,
                              discovery: undefined
                            }
                          }))
                          // Clear configured folders
                          setConfiguredFolders({})
                          setSelectedFolders({ product: new Set(), customer: new Set() })
                          // Clear mock config from localStorage
                          localStorage.removeItem('google_drive_config_mock')
                          
                          setToast({
                            id: Date.now(),
                            message: 'Google Drive disconnected successfully'
                          })
                          setTimeout(() => setToast(null), 3000)
                        } catch (error) {
                          console.error('[Settings] Error disconnecting:', error)
                          setToast({
                            id: Date.now(),
                            message: 'Failed to disconnect Google Drive. Please try again.'
                          })
                          setTimeout(() => setToast(null), 5000)
                        }
                      } else {
                        // "Connect" button - initiate OAuth
                        if (authLoading) {
                          return
                        }
                        if (!user?.id) {
                          navigate('/login')
                          return
                        }
                        if (import.meta.env.DEV) {
                          console.debug('[Settings] Initiating OAuth with user ID:', user.id)
                        }
                        apiService.oauth.authorizeGoogle(user.id)
                      }
                    }}
                    disabled={authLoading && !realConnectionStatus['google']?.connected}
                  >
                    {realConnectionStatus['google']?.connected ? 'Disconnect' : 'Connect'}
                  </Button>
                  {/* Show "Configure Folders" button when connected */}
                  {realConnectionStatus['google']?.connected && (
                    <Button
                      variant="secondary"
                      className="flex-1"
                      onClick={() => {
                        setShowFolderConfig(true)
                        setFolderConfigTab('product')
                      }}
                    >
                      Configure Folders
                    </Button>
                  )}
                </div>
                
                {/* Folder Configuration Status */}
                {(configuredFolders.product || configuredFolders.customer) && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-700 mb-2">Folder Configuration:</p>
                    <div className="space-y-1.5">
                      {configuredFolders.product && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-medium text-gray-700">Product Insights:</span>
                          <span className="text-gray-600">{configuredFolders.product.folder_name}</span>
                        </div>
                      )}
                      {configuredFolders.customer && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-medium text-gray-700">Customer Insights:</span>
                          <span className="text-gray-600">{configuredFolders.customer.folder_name}</span>
                        </div>
                      )}
                      {!configuredFolders.product && !configuredFolders.customer && (
                        <div className="text-xs text-gray-500 italic">No folders configured</div>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">
                      MVP: One folder per domain is applied today. Choose a different folder to replace the current one.
                    </p>
                  </div>
                )}
                
                {/* MVP: Test buttons to simulate OAuth redirect (remove in production) */}
                {import.meta.env.DEV && (
                  <div className="mt-2 flex flex-col gap-2">
                    <div className="text-xs text-gray-500 mb-1">[DEV] Test OAuth Flow:</div>
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          // Simulate OAuth success by setting mock connection status directly
                          // This allows you to see the connected state without page reload
                          setRealConnectionStatus(prev => ({
                            ...prev,
                            google: {
                              id: 'google',
                              connected: true,
                              discovery: {
                                status: 'completed',
                                completed_at: new Date().toISOString()
                              }
                            }
                          }))
                          // Load config after connection (will load from localStorage if available)
                          await loadCurrentConfig()
                          // Show success toast
                          setToast({
                            id: Date.now(),
                            message: 'Google connected successfully! You can now explore and configure folders for insights.'
                          })
                          // Auto-dismiss toast after 5 seconds
                          setTimeout(() => setToast(null), 5000)
                        }}
                        className="text-xs px-2 py-1 text-blue-600 hover:text-blue-700 underline border border-blue-200 rounded"
                      >
                        [DEV] Test Success
                      </button>
                      <button
                        onClick={() => {
                          // Simulate OAuth error
                          const errorToast = {
                            id: Date.now(),
                            message: 'Failed to connect Google. Access denied. Please try again.'
                          }
                          setToast(errorToast)
                          setTimeout(() => {
                            setToast(null)
                          }, 5000)
                        }}
                        className="text-xs px-2 py-1 text-red-600 hover:text-red-700 underline border border-red-200 rounded"
                      >
                        [DEV] Test Error
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Manual Upload Card */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <UploadCloud className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Manual Upload</h3>
                <p className="text-sm text-gray-600 mb-6">Upload files directly for customer or product insights processing.</p>
                
                {/* Hidden file inputs */}
                <input
                  ref={fileInputRefs.customer}
                  type="file"
                  multiple
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => {
                    console.log('[Settings] File input onChange triggered for customer')
                    const fileList = e.target.files
                    console.log('[Settings] e.target.files:', fileList)
                    console.log('[Settings] e.target.files.length:', fileList?.length)
                    console.log('[Settings] e.target.files type:', typeof fileList)
                    if (fileList) {
                      const filesArray = Array.from(fileList)
                      console.log('[Settings] Converted to array:', filesArray)
                      console.log('[Settings] Array length:', filesArray.length)
                      console.log('[Settings] File names:', filesArray.map(f => f.name))
                      // Store files in a variable to prevent FileList from being cleared
                      handleFileUpload('customer', fileList)
                    } else {
                      console.warn('[Settings] No files in e.target.files')
                    }
                  }}
                />
                <input
                  ref={fileInputRefs.product}
                  type="file"
                  multiple
                  accept=".csv,.docx,.md,.txt"
                  className="hidden"
                  onChange={(e) => handleFileUpload('product', e.target.files)}
                />
                
                {/* Customer Insights Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">Customer Insights</h4>
                      <p className="text-xs text-gray-500 mt-0.5">CSV only</p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        console.log('[Settings] Upload button clicked for customer insights')
                        console.log('[Settings] fileInputRefs.customer.current:', fileInputRefs.customer.current)
                        if (fileInputRefs.customer.current) {
                          fileInputRefs.customer.current.click()
                        } else {
                          console.error('[Settings] fileInputRefs.customer.current is null!')
                        }
                      }}
                      disabled={uploading.customer}
                    >
                      {uploading.customer ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                  {uploadedFiles.customer.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {(expandedFiles.customer ? uploadedFiles.customer : uploadedFiles.customer.slice(0, 2)).map((file) => (
                        <div key={file.id} className="space-y-0.5">
                          <div className="flex items-center gap-2 text-xs">
                            <File className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600 flex-1 min-w-0 truncate">{file.name}</span>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {file.status === 'uploading' && (
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-tandem-blue"></div>
                              )}
                              {file.status === 'success' && (
                                <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                              )}
                              {file.status === 'error' && (
                                <XCircle className="w-3.5 h-3.5 text-red-600" />
                              )}
                              {file.status !== 'uploading' && (
                                <button
                                  onClick={() => {
                                    setUploadedFiles(prev => ({
                                      ...prev,
                                      customer: prev.customer.filter(f => f.id !== file.id)
                                    }))
                                  }}
                                  className="p-0.5 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Remove file"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                          {file.status === 'error' && file.errorMessage && (
                            <div className="text-[10px] text-red-600 pl-5">{file.errorMessage}</div>
                          )}
                        </div>
                      ))}
                      {uploadedFiles.customer.length > 2 && (
                        <div className="flex items-center justify-between pl-5 pt-1">
                          <button
                            onClick={() => setExpandedFiles(prev => ({ ...prev, customer: !prev.customer }))}
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            {expandedFiles.customer ? (
                              <>
                                <ChevronUp className="w-3 h-3" />
                                Show less
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-3 h-3" />
                                Show {uploadedFiles.customer.length - 2} more file{uploadedFiles.customer.length - 2 !== 1 ? 's' : ''}
                              </>
                            )}
                          </button>
                          {expandedFiles.customer && uploadedFiles.customer.length > 0 && (
                            <button
                              onClick={() => {
                                if (confirm(`Remove all ${uploadedFiles.customer.length} file(s)?`)) {
                                  setUploadedFiles(prev => ({ ...prev, customer: [] }))
                                }
                              }}
                              className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                            >
                              <Trash2 className="w-3 h-3" />
                              Remove all
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Product Insights Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">Product Insights</h4>
                      <p className="text-xs text-gray-500 mt-0.5">CSV, DOCX, MD, TXT</p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => fileInputRefs.product.current?.click()}
                      disabled={uploading.product}
                    >
                      {uploading.product ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                  {uploadedFiles.product.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {(expandedFiles.product ? uploadedFiles.product : uploadedFiles.product.slice(0, 2)).map((file) => (
                        <div key={file.id} className="space-y-0.5">
                          <div className="flex items-center gap-2 text-xs">
                            <File className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600 flex-1 min-w-0 truncate">{file.name}</span>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {file.status === 'uploading' && (
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-tandem-blue"></div>
                              )}
                              {file.status === 'success' && (
                                <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                              )}
                              {file.status === 'error' && (
                                <XCircle className="w-3.5 h-3.5 text-red-600" />
                              )}
                              {file.status !== 'uploading' && (
                                <button
                                  onClick={() => {
                                    setUploadedFiles(prev => ({
                                      ...prev,
                                      product: prev.product.filter(f => f.id !== file.id)
                                    }))
                                  }}
                                  className="p-0.5 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Remove file"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                          {file.status === 'error' && file.errorMessage && (
                            <div className="text-[10px] text-red-600 pl-5">{file.errorMessage}</div>
                          )}
                        </div>
                      ))}
                      {uploadedFiles.product.length > 2 && (
                        <div className="flex items-center justify-between pl-5 pt-1">
                          <button
                            onClick={() => setExpandedFiles(prev => ({ ...prev, product: !prev.product }))}
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            {expandedFiles.product ? (
                              <>
                                <ChevronUp className="w-3 h-3" />
                                Show less
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-3 h-3" />
                                Show {uploadedFiles.product.length - 2} more file{uploadedFiles.product.length - 2 !== 1 ? 's' : ''}
                              </>
                            )}
                          </button>
                          {expandedFiles.product && uploadedFiles.product.length > 0 && (
                            <button
                              onClick={() => {
                                if (confirm(`Remove all ${uploadedFiles.product.length} file(s)?`)) {
                                  setUploadedFiles(prev => ({ ...prev, product: [] }))
                                }
                              }}
                              className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                            >
                              <Trash2 className="w-3 h-3" />
                              Remove all
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pipeline History Section - Commented out for MVP */}
            {false && (
            <div className="mt-8">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Pipeline History</h2>
                <p className="text-sm text-gray-600">Review recent pipeline runs and their status.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-base font-semibold text-gray-900">Customer Insights</h4>
                    <span className="text-xs px-2 py-1 rounded-full border bg-green-50 text-green-700 border-green-200">Completed</span>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>Last run: {humanize(new Date(Date.now() - 5*3600_000).toISOString())}</p>
                    <p>Records processed: <span className="font-medium text-gray-900">1,234</span></p>
                    <p className="text-xs text-gray-500">Status: Processing completed successfully</p>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-base font-semibold text-gray-900">Product Insights</h4>
                    <span className="text-xs px-2 py-1 rounded-full border bg-green-50 text-green-700 border-green-200">Completed</span>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>Last run: {humanize(new Date(Date.now() - 2*3600_000).toISOString())}</p>
                    <p>Records processed: <span className="font-medium text-gray-900">567</span></p>
                    <p className="text-xs text-gray-500">Status: Processing completed successfully</p>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* MVP: Commented out complex integrations table - too many datasources for MVP pilot */}
            {false && (
            <div className="overflow-x-auto bg-white rounded-lg shadow">
              <table className="min-w-full table-fixed divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gradient-to-r from-indigo-600 via-blue-600 to-sky-600 text-white">
                    <th className="relative w-48 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <button className="inline-flex items-center gap-1" onClick={() => toggleSort('name')}>
                          <span>Datasource</span>
                          {sortBy === 'name' ? (sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-white/90" /> : <ChevronDown className="w-3.5 h-3.5 text-white/90" />) : <ArrowUpDown className="w-3.5 h-3.5 text-white/70" />}
                        </button>
                        <button className="ml-1 text-white/80 hover:text-white" onClick={() => setOpenNameFilter(v => !v)} aria-label="Search datasources">
                          <Search className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {openNameFilter && (
                        <div className="absolute mt-2 bg-white text-gray-800 rounded-md shadow-lg p-2 z-10">
                          <div className="relative">
                            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                            <input
                              autoFocus
                              className="h-8 pl-7 pr-2 w-56 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-tandem-blue/30"
                              placeholder="Search datasources"
                              value={nameFilter}
                              onChange={e => setNameFilter(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') setOpenNameFilter(false) }}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th className="w-96 px-4 py-3 text-left text-xs font-medium tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <button className="inline-flex items-center gap-1" onClick={() => toggleSort('product')}>
                          <span>Product Insights</span>
                          {sortBy === 'product' ? (sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-white/90" /> : <ChevronDown className="w-3.5 h-3.5 text-white/90" />) : <ArrowUpDown className="w-3.5 h-3.5 text-white/70" />}
                        </button>
                        <button className="ml-1 text-white/80 hover:text-white" onClick={() => setOpenFilter(openFilter === 'product' ? null : 'product')} aria-label="Filter product">
                          <Filter className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {openFilter === 'product' && (
                        <div className="absolute mt-2 bg-white text-gray-800 rounded-md shadow-lg p-2 z-10">
                          <select className="h-8 text-xs px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-tandem-blue/30 w-auto" value={productFilter} onChange={e => { setProductFilter(e.target.value as any); setOpenFilter(null) }}>
                            <option value="any">Any</option>
                            <option value="connected">Connected</option>
                            <option value="issues">Issues</option>
                            <option value="not_connected">Not Connected</option>
                            <option value="not_supported">Not Supported</option>
                            <option value="coming_soon">Coming Soon</option>
                          </select>
                        </div>
                      )}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <button className="inline-flex items-center gap-1" onClick={() => toggleSort('customer')}>
                          <span>Customer Insights</span>
                          {sortBy === 'customer' ? (sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-white/90" /> : <ChevronDown className="w-3.5 h-3.5 text-white/90" />) : <ArrowUpDown className="w-3.5 h-3.5 text-white/70" />}
                        </button>
                        <button className="ml-1 text-white/80 hover:text-white" onClick={() => setOpenFilter(openFilter === 'customer' ? null : 'customer')} aria-label="Filter customer">
                          <Filter className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {openFilter === 'customer' && (
                        <div className="absolute mt-2 bg-white text-gray-800 rounded-md shadow-lg p-2 z-10">
                          <select className="h-8 text-xs px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-tandem-blue/30 w-auto" value={customerFilter} onChange={e => { setCustomerFilter(e.target.value as any); setOpenFilter(null) }}>
                            <option value="any">Any</option>
                            <option value="connected">Connected</option>
                            <option value="issues">Issues</option>
                            <option value="not_connected">Not Connected</option>
                            <option value="not_supported">Not Supported</option>
                            <option value="coming_soon">Coming Soon</option>
                          </select>
                        </div>
                      )}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <button className="inline-flex items-center gap-1" onClick={() => toggleSort('status')}>
                          <span>Status</span>
                          {sortBy === 'status' ? (sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-white/90" /> : <ChevronDown className="w-3.5 h-3.5 text-white/90" />) : <ArrowUpDown className="w-3.5 h-3.5 text-white/70" />}
                        </button>
                        <button className="ml-1 text-white/80 hover:text-white" onClick={() => setOpenFilter(openFilter === 'status' ? null : 'status')} aria-label="Filter status">
                          <Filter className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {openFilter === 'status' && (
                        <div className="absolute mt-2 bg-white text-gray-800 rounded-md shadow-lg p-2 z-10">
                          <select className="h-8 text-xs px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-tandem-blue/30 w-auto" value={statusFilter} onChange={e => { setStatusFilter(e.target.value as any); setOpenFilter(null) }}>
                            <option value="any">Any</option>
                            <option value="needs_review">Action needed</option>
                            <option value="preparing">Preparing</option>
                            <option value="active">Active</option>
                            <option value="paused">Paused</option>
                            <option value="discovery_running">Discovery running</option>
                          </select>
                        </div>
                      )}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(ds => (
                    <React.Fragment key={ds.id}>
                      <tr className={`cursor-pointer ${expandedId === ds.id ? 'bg-blue-50/40' : 'hover:bg-gray-50'}`} onClick={() => onEdit(ds)}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <img src={ds.logo} alt={ds.name} className="w-6 h-6" onError={(e: any)=>{ e.currentTarget.style.display='none' }} />
                            <div className="text-sm font-medium text-gray-900 max-w-[10rem] truncate flex items-center gap-2" title={ds.name}>
                              <span className="truncate">{ds.name}</span>
                              { (ds.status.product === 'connected' || ds.status.customer === 'connected') && <CheckCircle className="w-4 h-4 text-green-600 shrink-0" aria-label="Connected" /> }
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-normal">{ds.description}</td>
                        <td className="px-6 py-3 whitespace-nowrap"><div className="flex items-center">{DOMAIN_STATUS_CHIP[ds.supports.product ? ds.status.product : 'not_supported']()}</div></td>
                        <td className="px-6 py-3 whitespace-nowrap"><div className="flex items-center">{DOMAIN_STATUS_CHIP[ds.supports.customer ? ds.status.customer : 'not_supported']()}</div></td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                          {(() => {
                            const state = jobStateById[ds.id] || 'idle'
                            return (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                                state === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
                                state === 'paused' ? 'bg-slate-50 text-slate-700 border-slate-200' :
                                state === 'needs_review' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                state === 'discovery_running' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                'bg-gray-50 text-gray-600 border-gray-200'
                              }`}>{FRIENDLY_STATUS[state]}</span>
                            )
                          })()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="flex justify-start gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button variant={needsConnect(ds) ? 'primary' : 'secondary'} onClick={() => (needsConnect(ds) ? onConnect(ds) : onEdit(ds))}>{primaryLabel(ds)}</Button>
                            {/* Discovery refresh button removed - discovery module is from backend_v2 (legacy) and not part of current MVP */}
                            <button
                              className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-gray-100 text-gray-700"
                              onClick={() => onEdit(ds)}
                              aria-label={expandedId === ds.id ? 'Collapse' : 'Expand'}
                              title={expandedId === ds.id ? 'Collapse' : 'Expand'}
                            >
                              {expandedId === ds.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </div>
                          {ds.lastError && <div className="text-xs text-red-600 mt-2">{ds.lastError}</div>}
                        </td>
                      </tr>
                      {expandedId === ds.id && (
                        <tr className="bg-blue-50/40 border-t-0">
                          <td colSpan={6} className="px-6 pb-6">
                            <div className="mt-2">
                               {/* Details Header (flattened into expanded pane) */}
                               <div className="px-2 py-2 flex items-center justify-between">
                                 <div className="flex items-center gap-3">
                                   {jobStateById[ds.id] && (
                                     <span className={`text-xs px-2 py-0.5 rounded-full border ${
                                       jobStateById[ds.id] === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
                                       jobStateById[ds.id] === 'paused' ? 'bg-slate-50 text-slate-700 border-slate-200' :
                                       jobStateById[ds.id] === 'needs_review' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                       'bg-blue-50 text-blue-700 border-blue-200'
                                     }`}>
                                       {FRIENDLY_STATUS[jobStateById[ds.id]]}
                                     </span>
                                   )}
                                 </div>
                               </div>
 
                               {/* Tabs */}
                               <div className="px-2 pb-3">
                                <div className="flex items-center justify-between">
                                  <div className="inline-flex items-center gap-1 bg-gray-100 rounded-md p-1 text-sm">
                                    {(['summary','mapping'] as const).map(t => (
                                      <button key={t} className={`px-3 py-1 rounded ${detailsTab === t ? 'bg-white shadow font-medium' : 'text-gray-700 hover:bg-gray-200'}`} onClick={() => setDetailsTab(t)}>
                                        {t === 'summary' ? 'Overview' : 'Mapping'}
                                      </button>
                                    ))}
                                  </div>
                                  <div />
                                </div>
                              </div>
 
                              {/* Body */}
                              <div className="px-2 pb-2">
                                {detailsTab === 'summary' && (
                                  <div className="space-y-4 text-sm">
                                    {jobStateById[ds.id] === 'needs_review' && (
                                      <div className="border rounded p-3 bg-amber-50/40">
                                        <div className="font-medium mb-1">Action needed</div>
                                        <div className="text-gray-700 mb-2">{(discoveryMockById[ds.id]?.objects?.length ?? 0)} items to review before insights start.</div>
                                        <button className="btn-primary" onClick={() => setDetailsTab('mapping')}>Review</button>
                                      </div>
                                    )}

                                    <div className="border rounded p-3">
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="text-left text-gray-500">
                                              <th className="py-2 pr-3">Insights domain</th>
                                              <th className="py-2 pr-3">Mapping</th>
                                              <th className="py-2 pr-3">Sync</th>
                                              <th className="py-2 pr-3">Status</th>
                                              <th className="py-2 pr-3">Last sync</th>
                                              <th className="py-2 pr-3 text-left">Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody className="align-top text-gray-700">
                                            {ds.supports.product && (
                                              <tr className="border-t">
                                                <td className="py-2 pr-3 font-medium">Product</td>
                                                <td className="py-2 pr-3">
                                                  <div className="flex items-center gap-2 text-xs">
                                                    <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{(discoveryMockById[ds.id]?.objects?.length ?? 0)} objs</span>
                                                    <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{discoveryMockById[ds.id]?.fieldsSuggested ?? 0} fields</span>
                                                    <button className="btn-secondary btn-xs" onClick={() => setDetailsTab('mapping')}>View</button>
                                                  </div>
                                                </td>
                                                <td className="py-2 pr-3">{scheduleChip('Every 24h')}</td>
                                                <td className="py-2 pr-3"><div className="inline-flex items-center">{statusChipForDomain(ds.status.product)}</div></td>
                                                <td className="py-2 pr-3 text-gray-600">{humanize(new Date(Date.now() - 5*3600_000).toISOString())}</td>
                                                <td className="py-2 pr-3">
                                                  <div className="flex items-center gap-2">
                                                    <Button size="xs" variant="secondary" onClick={() => onEdit(ds)}>{ds.status.product === 'connected' ? 'Disconnect' : 'Connect'}</Button>
                                                    {jobStateById[ds.id] === 'active' ? (
                                                      <button className="btn-secondary btn-xs" onClick={() => onPauseResume(ds)}>Cancel</button>
                                                    ) : (
                                                      <button className="btn-secondary btn-xs" onClick={() => onSync(ds)}>Run</button>
                                                    )}
                                                    <button className="btn-secondary btn-xs" onClick={() => onEdit(ds)}>Edit Configuration</button>
                                                  </div>
                                                </td>
                                              </tr>
                                            )}

                                            {ds.supports.customer && (
                                              <tr className="border-t">
                                                <td className="py-2 pr-3 font-medium">Customer</td>
                                                <td className="py-2 pr-3">
                                                  <div className="flex items-center gap-2 text-xs">
                                                    <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{(discoveryMockById[ds.id]?.objects?.length ?? 0)} objs</span>
                                                    <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{discoveryMockById[ds.id]?.fieldsSuggested ?? 0} fields</span>
                                                    <button className="btn-secondary btn-xs" onClick={() => setDetailsTab('mapping')}>View</button>
                                                  </div>
                                                </td>
                                                <td className="py-2 pr-3">{scheduleChip('Every 24h')}</td>
                                                <td className="py-2 pr-3"><div className="inline-flex items-center">{statusChipForDomain(ds.status.customer)}</div></td>
                                                <td className="py-2 pr-3 text-gray-600">{humanize(new Date(Date.now() - 5*3600_000).toISOString())}</td>
                                                <td className="py-2 pr-3">
                                                  <div className="flex items-center gap-2">
                                                    <Button size="xs" variant="secondary" onClick={() => onEdit(ds)}>{ds.status.customer === 'connected' ? 'Disconnect' : 'Connect'}</Button>
                                                    {jobStateById[ds.id] === 'active' ? (
                                                      <button className="btn-secondary btn-xs" onClick={() => onPauseResume(ds)}>Cancel</button>
                                                    ) : (
                                                      <button className="btn-secondary btn-xs" onClick={() => onSync(ds)}>Run</button>
                                                    )}
                                                    <button className="btn-secondary btn-xs" onClick={() => onEdit(ds)}>Edit Configuration</button>
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {detailsTab === 'mapping' && (
                                  <div className="text-sm space-y-6">
                                    {ds.supports.product && (
                                      <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                          <div className="font-medium">Product</div>
                                          <div className="flex items-center gap-2 text-xs">
                                            <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{(discoveryMockById[ds.id]?.objects?.length ?? 0)} object types scanned</span>
                                            <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{discoveryMockById[ds.id]?.fieldsSuggested ?? 0} fields cataloged</span>
                                            <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{discoveryMockById[ds.id]?.filtersSuggested ?? 0} filters suggested</span>
                                          </div>
                                        </div>
                                        <div>
                                          <div className="font-medium mb-2">Objects</div>
                                          <table className="w-full text-sm">
                                            <thead>
                                              <tr className="text-left text-gray-500">
                                                <th className="py-1">Object</th>
                                                <th className="py-1">Sample</th>
                                                <th className="py-1">Include</th>
                                              </tr>
                                            </thead>
                                            <tbody className="align-top">
                                              {(discoveryMockById[ds.id]?.objects ?? []).map(o => (
                                                <tr key={o.name} className="border-t">
                                                  <td className="py-2">{o.name}</td>
                                                  <td className="py-2 text-gray-600">{o.sample}</td>
                                                  <td className="py-2"><input type="checkbox" defaultChecked={o.includeDefault} /></td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}
                                    {ds.supports.customer && (
                                      <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                          <div className="font-medium">Customer</div>
                                          <div className="flex items-center gap-2 text-xs">
                                            <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{(discoveryMockById[ds.id]?.objects?.length ?? 0)} object types scanned</span>
                                            <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{discoveryMockById[ds.id]?.fieldsSuggested ?? 0} fields cataloged</span>
                                            <span className="px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700 border-gray-200">{discoveryMockById[ds.id]?.filtersSuggested ?? 0} filters suggested</span>
                                          </div>
                                        </div>
                                        <div>
                                          <div className="font-medium mb-2">Filters</div>
                                          <div className="grid md:grid-cols-2 gap-3">
                                            {ds.id === 'google' && (
                                              <div className="border rounded p-3">
                                                <div className="font-medium mb-1">Gmail keywords</div>
                                                <div className="text-gray-600">Include: renewal, qbr, kpi • Exclude: no-reply, unsubscribe</div>
                                              </div>
                                            )}
                                            {ds.id === 'google' && (
                                              <div className="border rounded p-3">
                                                <div className="font-medium mb-1">Owner domain filter</div>
                                                <div className="text-gray-600">Include sender/recipient @acme.com</div>
                                              </div>
                                            )}
                                            {ds.id === 'google' && (
                                              <div className="border rounded p-3">
                                                <div className="font-medium mb-1">Drive locations</div>
                                                <div className="text-gray-600">/Finance/Reports, /Product/Specs</div>
                                              </div>
                                            )}
                                            {ds.id === 'slack' && (
                                              <div className="border rounded p-3">
                                                <div className="font-medium mb-1">Slack channels</div>
                                                <div className="text-gray-600">#sales, #exec, #product-updates</div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    {jobStateById[ds.id] === 'needs_review' && (
                                      <div><button className="btn-primary" onClick={() => onConfirmAndContinue(ds)}>Confirm and continue</button></div>
                                    )}
                                  </div>
                                )}
                                
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </>
        )}

        {/* MVP: Commented out for MVP pilot */}
        {false && activeTab === 'notifications' && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Alert Notification Settings</h2>
            <p className="text-sm text-gray-600">Configure how and when you receive notifications. (mock)</p>
          </div>
        )}

        {false && activeTab === 'dashboard' && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Setup Dashboard</h2>
            <p className="text-sm text-gray-600">Overview of integration health and recent activity. (mock)</p>
          </div>
        )}
        </div>
      </div>
      
      {/* Folder Configuration Modal */}
      {showFolderConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFolderConfig(false)}></div>
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Configure Google Drive Folders</h2>
                <p className="text-sm text-gray-600 mt-1">Select folders to sync for Product Insights and Customer Insights</p>
              </div>
              <button
                onClick={() => setShowFolderConfig(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Tabs */}
            <div className="px-6 pt-4 border-b border-gray-200">
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setFolderConfigTab('product')
                    setFolderSearchQuery('') // Clear search when switching tabs
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    folderConfigTab === 'product'
                      ? 'bg-white border-t border-l border-r border-gray-200 text-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Product Insights
                </button>
                <button
                  onClick={() => {
                    setFolderConfigTab('customer')
                    setFolderSearchQuery('') // Clear search when switching tabs
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    folderConfigTab === 'customer'
                      ? 'bg-white border-t border-l border-r border-gray-200 text-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Customer Insights
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-4 space-y-1">
                <p className="text-sm text-gray-600">
                  Select which folders contain {folderConfigTab === 'product' ? 'product-related' : 'customer-related'} documents. 
                  You can leave folders unselected. Changes will be saved when you click "Save Configuration".
                </p>
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  MVP note: We currently support one folder per domain. Saving a new folder will replace the previous selection.
                </p>
              </div>
              
              {/* Search and Refresh */}
              <div className="mb-4 flex gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search folders..."
                    value={folderSearchQuery}
                    onChange={(e) => setFolderSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tandem-blue/30"
                  />
                </div>
                <button
                  onClick={() => {
                    setFolderSearchQuery('')
                    loadFolders()
                  }}
                  disabled={loadingFolders}
                  className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm text-gray-700"
                  title="Refresh folder list"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingFolders ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
              </div>
              
              {/* Folder Tree */}
              <div className="border border-gray-200 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                {loadingFolders ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tandem-blue mx-auto mb-2"></div>
                    <p>Loading folders...</p>
                  </div>
                ) : folders[folderConfigTab].length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No folders found. Make sure Google Drive is connected.</p>
                  </div>
                ) : (() => {
                  // Filter folders based on search query
                  const filteredFolders = folders[folderConfigTab].filter(folder => {
                    if (!folderSearchQuery.trim()) return true
                    const query = folderSearchQuery.toLowerCase()
                    return folder.folder_name.toLowerCase().includes(query) ||
                           (folder.sample_files || []).some(file => file.toLowerCase().includes(query))
                  })
                  
                  if (filteredFolders.length === 0) {
                    return (
                      <div className="text-center py-8 text-gray-500">
                        <p>No folders match "{folderSearchQuery}"</p>
                        <button
                          onClick={() => setFolderSearchQuery('')}
                          className="mt-2 text-sm text-tandem-blue hover:underline"
                        >
                          Clear search
                        </button>
                      </div>
                    )
                  }
                  
                  return filteredFolders.map(folder => {
                    const isSelected = selectedFolders[folderConfigTab].has(folder.folder_id)
                    const disableCheckbox = selectedFolders[folderConfigTab].size >= 1 && !isSelected
                    
                    return (
                      <div key={folder.folder_id} className="mb-3 pb-3 border-b border-gray-100 last:border-0">
                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={disableCheckbox}
                            onChange={(e) => {
                              const newSelected = new Set(selectedFolders[folderConfigTab])
                              if (e.target.checked) {
                                newSelected.add(folder.folder_id)
                              } else {
                                newSelected.delete(folder.folder_id)
                              }
                              setSelectedFolders(prev => ({
                                ...prev,
                                [folderConfigTab]: newSelected
                              }))
                            }}
                            className={`w-4 h-4 text-tandem-blue border-gray-300 rounded focus:ring-tandem-blue mt-0.5 ${disableCheckbox ? 'opacity-40 cursor-not-allowed' : ''}`}
                          />
                          <Folder className="w-4 h-4 text-blue-500 mt-0.5" />
                          <div className="flex-1">
                            <label 
                              className="text-sm font-medium text-gray-900 cursor-pointer block" 
                              onClick={(e) => {
                                if (disableCheckbox) return
                                e.preventDefault()
                                const checkbox = e.currentTarget.previousElementSibling?.previousElementSibling as HTMLInputElement
                                if (checkbox) checkbox.click()
                              }}
                            >
                              {folder.folder_name}
                            </label>
                            {folder.sample_files && folder.sample_files.length > 0 && (
                              <div className="mt-1.5">
                                <p className="text-xs text-gray-500 mb-1">Sample files:</p>
                                <div className="flex flex-wrap gap-1">
                                  {folder.sample_files.slice(0, 3).map((fileName, idx) => (
                                    <span key={idx} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                      {fileName}
                                    </span>
                                  ))}
                                  {folder.sample_files.length > 3 && (
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">
                                      +{folder.sample_files.length - 3} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })
                })()}
              </div>
              
              {/* Selected Summary */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  {selectedFolders[folderConfigTab].size} folder{selectedFolders[folderConfigTab].size !== 1 ? 's' : ''} selected
                </p>
                <p className="text-xs text-gray-600">
                  Changes will be saved automatically when you close this dialog.
                </p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => setShowFolderConfig(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={saveFolderConfig}
                disabled={savingConfig || loadingFolders}
              >
                {savingConfig ? 'Saving...' : 'Save Configuration'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
